import { useSyncExternalStore } from 'react';

// Analyses on their way, by clip id (or a new clip's draft id). They live outside any component, so
// opening another clip doesn't lose one: its chat shows the analyzing card while it runs, and picks
// up the result (or why it failed) when it opens again.
//
// An entry is { status: 'running' | 'done' | 'failed', info, result, error }. `info` is what the card
// shows: { filename, note, version, startedAt, name, category, instrumentType }, and while it runs
// { sent, total, uploadedAt } as the file uploads, `waveform` once the file is read (null if the
// browser couldn't read it), and the server's `step` ('unpack', 'measure' or 'write') with `checks`,
// what each finished check found ({ level: 'peaks at −1.2 dB' }). A done or failed entry waits for the clip's chat to take it (take()); a
// draft's is dropped as soon as it settles.

const entries = new Map();
const listeners = new Set();
let running = [];
// When each version's score arrived, so a ring shown right after fills in instead of just appearing
const fresh = new Map();
const FRESH_MS = 5000;

const emit = () => {
  running = [...entries].filter(([, entry]) => entry.status === 'running').map(([key]) => key);
  listeners.forEach((listener) => listener());
};

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/**
 * Track an analysis for `key`. Resolves and rejects as `promise` does. With `keep: false` (a draft
 * clip, which gets a real id once done) the entry goes away as soon as it settles.
 */
export const startAnalysis = (key, info, promise, { keep = true } = {}) => {
  entries.set(key, { status: 'running', info });
  emit();
  promise.then(
    (result) => {
      if (keep) entries.set(key, { status: 'done', info, result });
      else entries.delete(key);
      emit();
    },
    (error) => {
      if (keep) entries.set(key, { status: 'failed', info, error });
      else entries.delete(key);
      emit();
    }
  );
  return promise;
};

/** Add to a running analysis's info */
export const updateAnalysis = (key, changes) => {
  const entry = entries.get(key);
  if (entry?.status !== 'running') return;
  entries.set(key, { ...entry, info: { ...entry.info, ...changes } });
  emit();
};

/** postAnalysis's onUploadProgress for `key`: keeps the bytes sent, and when the last one went */
export const uploadProgress = (key) => (sent, total) => {
  const uploaded = sent >= total && !entries.get(key)?.info.uploadedAt;
  updateAnalysis(key, { sent, total, ...(uploaded && { uploadedAt: Date.now() }) });
};

/** postAnalysis's onEvent for `key`: keeps the step it's on, and what each finished check found */
export const analysisStep = (key) => (event) => {
  if (event.step === 'check') {
    const checks = entries.get(key)?.info.checks;
    updateAnalysis(key, { checks: { ...checks, [event.check]: event.summary || 'done' } });
  } else {
    updateAnalysis(key, { step: event.step, ...(event.focus && { focus: event.focus }) });
  }
};

/** Note that a version's score just arrived */
export const markFresh = (versionId) => fresh.set(versionId, Date.now());

/** Whether a version's score arrived in the last few seconds */
export const isFresh = (versionId) => Date.now() - (fresh.get(versionId) || 0) < FRESH_MS;

/** Hand over a settled analysis once its clip's chat has shown it */
export const takeAnalysis = (key) => {
  const entry = entries.get(key);
  if (entry && entry.status !== 'running') {
    entries.delete(key);
    emit();
  }
};

/** The analysis for one clip, if any; re-renders when it changes */
export const useAnalysis = (key) => useSyncExternalStore(subscribe, () => entries.get(key));

/** The ids of clips with an analysis running, for the sidebar */
export const useRunningAnalyses = () => useSyncExternalStore(subscribe, () => running);

/** For tests: forget everything */
export const resetAnalyses = () => {
  entries.clear();
  fresh.clear();
  emit();
};
